<template>
  <v-dialog v-model="visible" fullscreen dark persistent transition="dialog-bottom-transition" hide-overlay>
    <div class="mobile-wizard-container">
      <div class="wizard-header">
        <div class="d-flex justify-center align-center">
          <img src="/img/roody_logo.png" height="65px" contain />

          <v-card-title class="pa-0 font-weight-bold">
            | {{ nutrition ? "Fragebogen Ernährung" : variant ? "Fragebogen" : "Anamnesebogen" }}
          </v-card-title>
        </div>
      </div>
      <v-progress-linear :value="progressValue" color="primary" height="6" class="mobile-wizard-progress"></v-progress-linear>

      <div class="mobile-wizard-content">
        <v-form @submit.prevent ref="anamneseForm" class="ma-auto" style="max-width: 800px" lazy-validation novalidate>
          <v-window v-model="anamneseFormStepper" :touchless="!viewOnly" :continuous="false">
            <v-window-item :value="1">
              <div class="wizard-step-content">
                <h1 class="text-h4 font-weight-bold">Benutzer-Auswahl</h1>
                <!-- HISTORY PANEL -->
                <v-card v-if="historyEntries && historyEntries.length" class="mt-6" outlined color="grey darken-3">
                  <v-card-title class="text-subtitle-1 font-weight-bold"> Änderungsverlauf </v-card-title>

                  <v-divider />

                  <v-card-text style="max-height: 250px; overflow-y: auto">
                    <div v-for="(entry, index) in historyEntries" :key="'history' + index" class="mb-4">
                      <div class="d-flex align-center">
                        <v-icon small class="mr-2">mdi-account-edit</v-icon>

                        <strong>
                          {{ getUserName(entry.changedBy) }}
                        </strong>

                        <span class="ml-2 grey--text text--lighten-1"> – {{ formatDate(entry.changedAt) }} </span>
                      </div>

                      <div class="text-caption grey--text text--lighten-2 ml-6">
                        {{ entry.note || "Anamnesebogen angepasst" }}
                      </div>
                    </div>
                  </v-card-text>
                </v-card>
                <p class="body-1 grey--text text--lighten-1 mt-2">
                  {{
                    nutrition && !variant
                      ? "Wähle den Ernährungscoach und einen Kunden, um zu beginnen."
                      : "Wähle einen Trainer und einen Kunden aus, um zu beginnen."
                  }}
                </p>

                <div v-if="!disableUserFields" class="mt-8">
                  <user-search
                    filled
                    dark
                    rounded
                    :value="formData.creator"
                    ignoreRights
                    :disabled="viewOnly || disableUserFields"
                    instructor
                    label="Visum (Anamnesebogen ersteller)"
                    @update="updateUser($event, 'creator')"></user-search>
                  <user-search
                    v-if="formData.creator"
                    filled
                    dark
                    rounded
                    class="mt-4"
                    ignoreRights
                    :value="formData.customer"
                    :disabled="viewOnly || disableUserFields"
                    label="Kunde"
                    @update="updateUser($event, 'customer')"></user-search>
                </div>
                <div v-else class="mt-8">
                  <v-chip large class="ma-2">
                    <v-icon left>mdi-account-check</v-icon>
                    Trainer: {{ creatorName }}
                  </v-chip>
                  <v-chip large class="ma-2">
                    <v-icon left>mdi-account-check</v-icon>
                    Kunde: {{ customerName }}
                  </v-chip>
                </div>

                <div class="mt-4 text-center">
                  <v-btn v-if="!viewOnly && !formData.customer && formData.creator" color="primary" @click="showRegisterDialog = true">
                    Neuen Account erstellen
                  </v-btn>
                  <v-btn rounded v-if="formData.customer && !viewOnly && !nutrition && !disableUserFields" class="mx-2" text @click="resetCustomer">
                    Kunde ändern
                  </v-btn>
                </div>
              </div>
            </v-window-item>

            <v-window-item :value="2 + pageIndex" :key="'page' + pageIndex" v-for="(page, pageIndex) in filteredPages">
              <div v-if="anamneseFormStepper === 2 + pageIndex" style="min-height: 60vh; display: flex; flex-direction: column">
                <v-card-title v-if="page.displayedTitle" style="word-break: break-word">
                  {{ page.displayedTitle }}
                </v-card-title>
                <v-row class="flex-column mx-0 px-1" :key="'page' + questionIndex" v-for="(question, questionIndex) in page.questions">
                  <v-card-title class="pb-0" style="word-break: break-word">
                    {{ question.title }}
                  </v-card-title>
                  <div v-if="getQuestionHistory(question._id).length > 0" class="mb-2 mt-1">
                    <v-menu open-on-hover bottom offset-y nudge-top="10">
                      <template v-slot:activator="{ on, attrs }">
                        <div v-bind="attrs" v-on="on" class="history-wrapper cursor-pointer">
                          <div class="d-flex align-center flex-wrap">
                            <v-icon x-small color="orange darken-2" class="mr-1">mdi-history</v-icon>
                            <span class="caption orange--text text--lighten-2 font-weight-bold">
                              <span class="caption orange--text text--lighten-2 font-weight-bold">
                                Zuletzt: "{{ truncateText(getLastChange(question._id).oldValue) || "Leer" }}"
                              </span>
                            </span>
                          </div>
                          <div class="caption grey--text text--lighten-1 ml-5">
                            {{ getUserName(getLastChange(question._id).changedBy) }} • {{ formatDate(getLastChange(question._id).changedAt) }}
                            <v-icon x-small color="grey">mdi-chevron-down</v-icon>
                          </div>
                        </div>
                      </template>

                      <v-list dense dark color="grey darken-4" elevation="10" border>
                        <v-subheader class="text-uppercase font-weight-black" style="height: 30px; font-size: 0.7rem">
                          Ganzer Änderungsverlauf
                        </v-subheader>
                        <v-divider></v-divider>
                        <v-list-item v-for="(log, lIdx) in getQuestionHistory(question._id)" :key="lIdx" class="py-1">
                          <v-list-item-content>
                            <v-list-item-title class="caption white--text">
                              <strong>{{ getUserName(log.changedBy) }}</strong>
                            </v-list-item-title>
                            <v-list-item-subtitle class="caption mt-1">
                              <span class="grey--text text--lighten-1">{{ log.oldValue || "Leer" }}</span>
                              <v-icon x-small class="mx-1">mdi-arrow-right</v-icon>
                              <span class="white--text">{{ log.newValue }}</span>
                            </v-list-item-subtitle>
                            <v-list-item-subtitle class="x-small-text grey--text mt-1">
                              {{ formatDate(log.changedAt) }}
                            </v-list-item-subtitle>
                          </v-list-item-content>
                        </v-list-item>
                      </v-list>
                    </v-menu>
                  </div>

                  <v-textarea
                    filled
                    auto-grow
                    rows="1"
                    :disabled="viewOnly"
                    rounded
                    :rules="question.required ? rules.required : []"
                    placeholder="TEXT EINGEBEN"
                    v-if="question.type === 'text'"
                    v-model="question.answer"></v-textarea>
                  <v-text-field
                    filled
                    rounded
                    :disabled="viewOnly"
                    :rules="question.required ? rules.required : []"
                    placeholder="ZAHL EINGEBEN"
                    v-if="question.type === 'number'"
                    v-model.number="question.answer"
                    :suffix="question.isPercent ? '%' : ''"></v-text-field>

                  <div class="d-flex flex-column justify-content-center my-4" v-if="question.type === 'rating'">
                    <v-rating
                      x-large
                      :disabled="viewOnly"
                      v-model="question.answer"
                      :length="question.maxStarCount"
                      color="yellow darken-3"
                      background-color="grey darken-1"
                      empty-icon="$ratingFull"
                      half-increments
                      hover
                      size="48"></v-rating>
                  </div>
                  <div class="my-3" v-if="question.type === 'select'">
                    <div :key="'selectOption' + index" v-for="(option, index) in question.options">
                      <input
                        :class="{ selectsDisabled: viewOnly }"
                        :disabled="viewOnly"
                        style="z-index: 1000"
                        :type="question.allowMultiple ? 'checkbox' : 'radio'"
                        :name="question._id"
                        :id="question._id + index"
                        @change="test(question)"
                        v-model="question.answer"
                        :value="option" />
                      <label style="z-index: 1000" :for="question._id + index">{{ option }}</label>
                      <div
                        class="ml-3"
                        v-if="
                          question.answer &&
                          question.answer.length &&
                          ((Array.isArray(question.answer) && question.answer.includes(option) && getAdditionalField(question, option)) ||
                            (question.answer === option && getAdditionalField(question, option)))
                        ">
                        <v-textarea
                          filled
                          auto-grow
                          rows="1"
                          :disabled="viewOnly"
                          rounded
                          :rules="getAdditionalField(question, option).required ? rules.required : []"
                          :label="getAdditionalField(question, option).title"
                          placeholder="TEXT EINGEBEN"
                          v-if="getAdditionalField(question, option).type === 'text' && question.additionalFieldAnswer"
                          v-model="question.additionalFieldAnswer[option]"></v-textarea>
                        <v-text-field
                          :disabled="viewOnly"
                          filled
                          rounded
                          :rules="getAdditionalField(question, option).required ? rules.required : []"
                          :label="getAdditionalField(question, option).title"
                          placeholder="ZAHL EINGEBEN"
                          v-if="getAdditionalField(question, option).type === 'number'"
                          v-model.number="question.additionalFieldAnswer[option]"
                          :suffix="question.isPercent ? '%' : ''"></v-text-field>
                        <div v-if="getAdditionalField(question, option).type === 'select'">
                          <div
                            :key="'additionalSelectOption' + additionalIndex"
                            v-for="(additionalOption, additionalIndex) in getAdditionalField(question, option).options">
                            <input
                              :class="{ selectsDisabled: viewOnly }"
                              :disabled="viewOnly"
                              style="z-index: 1000"
                              :type="getAdditionalField(question, option).allowMultiple ? 'checkbox' : 'radio'"
                              :name="question._id + 'additional'"
                              :id="question._id + 'additional' + additionalIndex + 1"
                              @change="test()"
                              v-model="question.additionalFieldAnswer[option]"
                              :value="additionalOption" />
                            <label style="z-index: 1000" :for="question._id + 'additional' + additionalIndex + 1">
                              {{ additionalOption }}
                            </label>
                          </div>
                          <div v-if="getAdditionalField(question, option).allowOwnInput">
                            <input
                              :disabled="viewOnly"
                              :class="{ selectsDisabled: viewOnly }"
                              style="z-index: 1000"
                              :type="getAdditionalField(question, option).allowMultiple ? 'checkbox' : 'radio'"
                              :name="question._id + 'additional'"
                              :id="question._id + 'additional' + additionalIndex + 1001"
                              @change="test(question, true)"
                              v-model="question.additionalFieldAnswer[option]"
                              :value="{
                                ownEntry: getValueForAdditionalField(question, option, getAdditionalField(question, option).allowMultiple),
                              }" />
                            <label style="z-index: 1000" :for="question._id + 'additional' + additionalIndex + 1001">
                              {{ "Eigene Eingabe" }}
                            </label>
                            <v-textarea
                              filled
                              rounded
                              auto-grow
                              rows="1"
                              :disabled="viewOnly"
                              placeholder="EIGENE EINGABE"
                              :rules="rules.required"
                              v-if="question.additionalFieldAnswer[option] && question.additionalFieldAnswer[option]['ownEntry']"
                              v-model="question.additionalFieldAnswer[option]['ownEntry']"></v-textarea>
                            <v-textarea
                              filled
                              rounded
                              rows="1"
                              auto-grow
                              :disabled="viewOnly"
                              placeholder="EIGENE EINGABE"
                              :rules="rules.required"
                              v-if="
                                question.additionalFieldAnswer[option] &&
                                question.additionalFieldAnswer[option].some((obj) => Object.keys(obj).includes('ownEntry'))
                              "
                              v-model="
                                question.additionalFieldAnswer[option][
                                  question.additionalFieldAnswer[option].findIndex((obj) => Object.keys(obj).includes('ownEntry'))
                                ].ownEntry
                              "></v-textarea>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </v-row>
              </div>
            </v-window-item>

            <v-window-item :value="filteredPages.length + 2">
              <v-card-title class="justify-content-center" v-if="settings && settings.disclaimer"> Haftungsausschluss </v-card-title>
              <v-card-text class="text-center">{{ settings && settings.disclaimer }}</v-card-text>
              <v-card-title class="justify-content-center">Unterschrift Kunde</v-card-title>
              <signature-pad
                :key="updateSignature"
                class="ma-auto"
                darkMode
                autoSaveSignature
                :disabled="viewOnly"
                :signatureData="viewOnly ? formData.customerSignature : null"
                @resetDrawing="formData.customerSignature = null"
                @saveSignature="(signature) => (formData.customerSignature = signature)"></signature-pad>
            </v-window-item>
            <v-window-item v-if="!disableUserFields" :value="filteredPages.length + 3">
              <v-card-title class="justify-content-center">Unterschrift Trainer</v-card-title>
              <signature-pad
                :key="updateSignature"
                class="ma-auto"
                darkMode
                :disabled="viewOnly"
                :signatureData="viewOnly ? formData.creatorSignature : null"
                autoSaveSignature
                @resetDrawing="formData.creatorSignature = null"
                @saveSignature="(signature) => (formData.creatorSignature = signature)"></signature-pad>
            </v-window-item>
          </v-window>
        </v-form>
      </div>

      <div class="mobile-wizard-nav">
        <v-btn text color="white" @click="handleBackStep" v-if="anamneseFormStepper > 1">
          <v-icon left>mdi-chevron-left</v-icon>
          Zurück
        </v-btn>
        <v-btn v-else text color="white" @click="setVisibleFalse"> Abbrechen </v-btn>
        <v-spacer></v-spacer>
        <v-btn
          depressed
          color="primary"
          @click="validateStepAndGoNext(anamneseFormStepper === 1 ? false : expressAnamnese)"
          :disabled="!stepFinished"
          :loading="submittingAnamnese"
          v-if="
            !(
              viewOnly &&
              (anamneseFormStepper === filteredPages.length + 3 || (disableUserFields && anamneseFormStepper === filteredPages.length + 2))
            )
          ">
          {{
            anamneseFormStepper === filteredPages.length + 3 || (disableUserFields && anamneseFormStepper === filteredPages.length + 2)
              ? "Abschliessen"
              : "Weiter"
          }}
          <v-icon right>mdi-chevron-right</v-icon>
        </v-btn>
      </div>
    </div>
    <register
      @register="newAccountRegistered()"
      :auto-login="false"
      :requirePassword="false"
      @close="showRegisterDialog = false"
      :active="showRegisterDialog"></register>
  </v-dialog>
</template>

<script>
import register from "@/views/launchScreen/register.vue"
import userSearch from "@/components/userSearch.vue"
import signaturePad from "@/components/signaturePad/signaturePad.vue"
import api from "@/api"

import { mapActions, mapGetters } from "vuex"

const GUESTBOOK_GOAL_OPTIONS = {
  weight_loss: ["Abnehmen", "Körperfettreduktion", "Gewicht reduzieren"],
  muscle_gain: ["Muskelaufbau"],
  toning: ["Straffen"],
  endurance: ["Ausdauer verbessern", "Ausdauer"],
  back_strength: ["Rücken kräftigen", "Rückenkräftigung"],
  pain_relief: ["Schmerzen beseitigen", "Schmerzen reduzieren"],
  movement: ["Bewegungsdrang ausleben"],
  mobility: ["Beweglichkeit verbessern", "Beweglichkeit"],
  coordination: ["Koordination verbessern", "Koordination"],
  health: ["Gesund bleiben", "Gesundheit"],
  posture: ["Haltung verbessern"],
  rehabilitation: ["Rehabilitation"],
  competition: ["Wettkampfvorbereitung"],
  other: ["Anderes Ziel", "Andere", "Sonstiges"],
}

const GUESTBOOK_REFERRAL_OPTIONS = {
  "Google Suche": ["Google Suche", "Internet"],
  "Google Werbung": ["Google Werbung", "Werbung"],
  Empfehlung: ["Empfehlung", "Empfehlung durch:"],
  "Tik Tok": ["Tik Tok", "TikTok"],
}

export default {
  components: {
    register,
    userSearch,
    signaturePad,
  },
  data() {
    return {
      historyEntries: [],
      creatorName: "",
      customerName: "",
      disableUserFields: false,
      updateSignature: 0,
      viewOnly: false,
      settings: {},
      nutrition: false,
      variant: null,
      expressAnamnese: false,
      visible: false,
      showRegisterDialog: false,
      anamneseFormStepper: 1,
      submittingAnamnese: false,
      pages: [],
      formData: {
        customerSignature: null,
        creator: null,
        customer: null,
        selection: null,
      },
      rules: {
        required: [(v) => !!v || "Dieses Feld ist erforderlich"],
        requiredArray: [(v) => !!v.length || "Mindestens eine Option auswählen"],
      },
    }
  },
  computed: {
    ...mapGetters("studioStore", ["getSelectedEmployee"]),
    ...mapGetters("panelStore", { selectedCustomer: "getSelectedCustomer" }),
    progressValue() {
      if (this.anamneseFormStepper === 1) return 10
      const totalSteps = this.filteredPages.length + (this.disableUserFields ? 2 : 3)
      return Math.round(((this.anamneseFormStepper - 1) / totalSteps) * 100)
    },
    stepFinished() {
      if (this.anamneseFormStepper === 1 && (!this.formData.creator || !this.formData.customer)) return false
      return true
    },
    filteredPages() {
      return this.pages.filter((page) => {
        if (this.expressAnamnese) {
          return page.showInExpress
        }
        return true
      })
    },
  },
  methods: {
    ...mapActions("navigationStoreNotPersisted", ["setNavigationbarVisibility"]),
    normalizePrefillText(value) {
      return String(value || "")
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .replace(/ß/g, "ss")
        .toLocaleLowerCase("de-CH")
        .replace(/[^a-z0-9]+/g, " ")
        .trim()
    },
    matchingQuestionOption(question, candidates) {
      const normalizedCandidates = (candidates || []).map((value) => this.normalizePrefillText(value)).filter(Boolean)
      return (question.options || []).find((option) => normalizedCandidates.includes(this.normalizePrefillText(option)))
    },
    applyGuestbookPrefill(prefill) {
      if (!prefill || typeof prefill !== "object") return
      const referralSource = String(prefill.referralSource || "").trim()
      const goals = Array.isArray(prefill.goals) ? prefill.goals : []

      this.pages.forEach((page) => {
        const questions = page.questions || []
        questions.forEach((question) => {
          if (question.type !== "select") return
          const title = this.normalizePrefillText(question.title)

          if (referralSource && title.includes("wie bist du auf uns aufmerksam geworden")) {
            const option = this.matchingQuestionOption(question, GUESTBOOK_REFERRAL_OPTIONS[referralSource] || [referralSource])
            if (option) this.$set(question, "answer", question.allowMultiple ? [option] : option)
            return
          }

          if (goals.length && title.includes("trainingsziel")) {
            const options = goals
              .map((goal) => this.matchingQuestionOption(question, GUESTBOOK_GOAL_OPTIONS[goal] || [goal]))
              .filter((option, index, values) => option && values.indexOf(option) === index)
            if (!options.length) return
            this.$set(question, "answer", question.allowMultiple ? options : options[0])
          }
        })
      })
    },
    truncateText(text) {
      if (!text) return ""
      return text.length > 40 ? text.substring(0, 37) + "..." : text
    },
    getLastChange(questionId) {
      if (!this.historyEntries) return null
      // Finde den aktuellsten Eintrag für genau diese Frage
      return this.historyEntries.filter((log) => log.questionId === questionId).sort((a, b) => new Date(b.changedAt) - new Date(a.changedAt))[0]
    },
    getQuestionHistory(questionId) {
      if (!this.historyEntries) return []
      return this.historyEntries
        .filter((log) => log.questionId === questionId && log.oldValue !== log.newValue) // Dubletten filtern
        .sort((a, b) => new Date(b.changedAt) - new Date(a.changedAt))
    },
    formatDate(date) {
      if (!date) return ""
      const d = new Date(date)
      return d.toLocaleString("de-CH")
    },

    getUserName(user) {
      if (!user) return "Unbekannt"
      if (typeof user === "object") {
        return `${user.vorname || ""} ${user.nachname || ""}`
      }
      return "Benutzer"
    },
    handleBackStep() {
      if (this.anamneseFormStepper > 1) {
        this.anamneseFormStepper--
      }
    },
    validateStepAndGoNext(express) {
      this.expressAnamnese = express
      if (this.viewOnly) {
        this.anamneseFormStepper++
        return
      }
      // VALIDATION FOR DEFAULT FORM FIELDS
      if (this.anamneseFormStepper > 1 && !this.$refs.anamneseForm.validate()) {
        setTimeout(() => {
          let errorElement = document.getElementsByClassName("error--text")
          if (errorElement && errorElement.length > 0) {
            errorElement[0].scrollIntoView({
              behavior: "smooth",
              block: "center",
            })
          }
        }, 100)
        return
      }
      // VALIDATION FOR SELECTS

      let error = false
      let activePage = this.filteredPages[this.anamneseFormStepper - 2]

      if (
        (this.anamneseFormStepper === this.filteredPages.length + 2 && !this.formData.customerSignature) ||
        (this.anamneseFormStepper === this.filteredPages.length + 3 && !this.disableUserFields && !this.formData.creatorSignature)
      ) {
        this.$toast.error("Bitte unterschreiben Sie das Formular")
        error = true
      }

      if (activePage && activePage.questions) {
        let activeSelectQuestions = activePage.questions.filter((question) => question.type === "select")
        if (activeSelectQuestions.length > 0) {
          activeSelectQuestions.forEach((question) => {
            if (question.required && !question.answer) {
              this.$toast.error("Bitte beantworte die Frage: " + question.title)
              error = true
            }
            if (question.additionalFields) {
              let additionalAnswerForQuestion = question.additionalFields.find((field) => field.selection === question.answer)
              if (
                additionalAnswerForQuestion &&
                additionalAnswerForQuestion.required &&
                (!question.additionalFieldAnswer || !question.additionalFieldAnswer[additionalAnswerForQuestion.selection])
              ) {
                this.$toast.error("Bitte beantworte die Zusatzfrage: " + additionalAnswerForQuestion.title)
                error = true
              }

              if (
                additionalAnswerForQuestion &&
                additionalAnswerForQuestion.required &&
                question.additionalFieldAnswer[additionalAnswerForQuestion.selection] &&
                question.additionalFieldAnswer[additionalAnswerForQuestion.selection]["ownEntry"] === " "
              ) {
                this.$toast.error("Bitte gib deine eigene Angabe für die Zusatzfrage : " + additionalAnswerForQuestion.title + "an.")
                error = true
              }
            }
          })
        }
      }

      if (!error) {
        if (
          this.anamneseFormStepper === this.filteredPages.length + 3 ||
          (this.disableUserFields && this.anamneseFormStepper === this.filteredPages.length + 2)
        ) {
          this.submitAnamnese()
          return
        }
        this.anamneseFormStepper++
      }
    },
    submitAnamnese() {
      const currentEditor = this.getSelectedEmployee?.user || null
      let data = {
        nutrition: this.nutrition,
        customer: this.formData.customer,
        creator: this.formData.creator,
        customerSignature: this.formData.customerSignature,
        creatorSignature: this.formData.creatorSignature,
        selection: this.formData.selection,
        pages: this.filteredPages,
        editorId: currentEditor,
        _id: this.formData._id,
      }
      if (this.variant) {
        data[this.variant] = true
      }
      this.submittingAnamnese = true
      api
        .submitAnamnese(data)
        .then((response) => {
          this.$toast.success("Der Anamnesebogen wurde erfolgreich erstellt.")
          this.$emit("submitted", this.formData.customer)
          this.setVisibleFalse()
        })
        .finally(() => {
          this.submittingAnamnese = false
        })
    },
    setVisibleFalse() {
      this.visible = false
      this.viewOnly = false
      this.anamneseFormStepper = 1
      this.formData = {
        customerSignature: null,
        creator: null,
        customer: null,
        selection: null,
      }
      this.updateSignature++
      this.submittingAnamnese = false
      if (!this.nutrition && !this.variant) {
        this.setNavigationbarVisibility(true)
      }
      this.nutrition = false
      this.variant = null
      this.disableUserFields = false
      this.creatorName = ""
      this.customerName = ""

      this.$emit("closeAnamnesebogen")
    },
    getAdditionalField(question, option) {
      return question.additionalFields && question.additionalFields.find((field) => field.selection === option)
    },
    getValueForAdditionalField(question, option, multiple = false) {
      if (multiple) {
        return question.additionalFieldAnswer[option] && question.additionalFieldAnswer[option].some((obj) => Object.keys(obj).includes("ownEntry"))
          ? question.additionalFieldAnswer[option][question.additionalFieldAnswer[option].findIndex((obj) => Object.keys(obj).includes("ownEntry"))]
              .ownEntry
          : " "
      } else {
        return question.additionalFieldAnswer[option] && question.additionalFieldAnswer[option]["ownEntry"]
          ? question.additionalFieldAnswer[option]["ownEntry"]
          : " "
      }
    },
    test(question = null, ownEntry = false) {
      if (question && ownEntry) {
      } else if (question && question.additionalFields.length) {
      }
      this.$forceUpdate()
    },
    resetCustomer() {
      this.formData.customer = null
    },

    async setVisible(id = null, editMode = false, nutrition = false, userID = null, coachID = null, guestbookPrefill = null) {
      if (nutrition && nutrition === true) this.nutrition = true
      if (nutrition && nutrition !== true) this.variant = nutrition
      this.setNavigationbarVisibility(false)
      this.visible = true
      let data = null

      // IDs aus store als Fallback verwenden
      const creatorIdFromStore = this.getSelectedEmployee?.user
      const customerIdFromStore = this.selectedCustomer?._id

      this.formData.creator = coachID || creatorIdFromStore || null
      this.formData.customer = userID || customerIdFromStore || null

      if (this.nutrition) {
        data = id ? await api.getAnamneseNutritionForUser(id) : await api.getAnamneseNutrition()
      } else if (this.variant) {
        data = id ? await api.getAnamneseVariantForUser(id, this.variant) : await api.getAnamneseVariant(this.variant)
      } else {
        data = id ? await api.getAnamneseForUser(id) : await api.getAnamneseForStudio()

        if (!data.pages.find((page) => !page.showInExpress)) {
          let completeAnamnese = await api.getAnamneseForStudio()
          if (completeAnamnese && completeAnamnese.pages && completeAnamnese.pages.find((page) => !page.showInExpress)) {
            ;(async () => {
              for (const page of completeAnamnese.pages) {
                for (const question of page.questions) {
                  question.additionalFieldAnswer = {}
                  if (question.allowMultiple) question.answer = []
                }
              }
              data.pages = completeAnamnese.pages.filter((page) => !page.showInExpress).concat(data.pages)
            })()
          }
        }
      }

      if (coachID && this.nutrition) {
        this.disableUserFields = true
        // fetch user names for display
        if (this.formData.creator) {
          const creatorData = await api.getUserForManager(this.formData.creator)
          this.creatorName = `${creatorData.vorname} ${creatorData.nachname}`
        }
        if (this.formData.customer) {
          const customerData = await api.getUserForManager(this.formData.customer)
          this.customerName = `${customerData.vorname} ${customerData.nachname}`
        }
        this.anamneseFormStepper = 2
      }

      if (creatorIdFromStore && customerIdFromStore) {
        this.anamneseFormStepper = 2
      }

      if (!data.pages || !data.pages.length) {
        this.$toast.error("Bitte prüfe dein Studio. In diesem Studio ist kein Anamnesebogen hinterlegt.")
        return
      }
      this.pages = data.pages
      this.pages.forEach((page) => {
        page.questions.forEach((question) => {
          if (!question.additionalFieldAnswer) {
            this.$set(question, "additionalFieldAnswer", {})
          }
          if (question.allowMultiple && !Array.isArray(question.answer)) {
            this.$set(question, "answer", [])
          }
        })
      })
      if (id) {
        this.formData = {
          _id: id,
          customer: data.customer,
          creator: data.creator,
          customerSignature: data.customerSignature,
          creatorSignature: data.creatorSignature,
        }
        if (editMode) {
          delete this.formData.customerSignature
          delete this.formData.creatorSignature
        } else {
          this.viewOnly = true
        }
        this.historyEntries = data.changeLog || []
      } else {
        this.pages.forEach((page) => {
          page.questions.forEach((question) => {
            question.additionalFieldAnswer = {}
            if (question.allowMultiple) question.answer = []
          })
        })
        this.historyEntries = []
        this.applyGuestbookPrefill(guestbookPrefill)
      }
      this.settings = data.settings
    },
    newAccountRegistered() {
      this.showRegisterDialog = false
      this.$toast.success("Neuer Account erfolgreich erstellt. Du kannst den Kunden nun auswählen")
    },
    updateUser(id, type) {
      this.formData[type] = id
      this.$forceUpdate() // Ensure reactivity
    },
  },
}
</script>

<style scoped lang="scss">
/* Wizard Container Styles */
.mobile-wizard-container {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: #121212;
  z-index: 1000;
  display: flex;
  flex-direction: column;
  color: white;
}
.wizard-header {
  padding: 1rem;
}
.mobile-wizard-progress {
  position: absolute;
  top: 88px;
  left: 0;
  width: 100%;
  z-index: 1;
  transition: all 0.4s ease-in-out;
}
.mobile-wizard-content {
  flex-grow: 1;
  overflow-y: auto;
  padding: 50px 24px 120px 24px; // Mehr padding unten für nav
}
.wizard-step-content {
  padding-top: 2rem;
}
.mobile-wizard-nav {
  position: fixed;
  bottom: 0;
  left: 0;
  width: 100%;
  padding: 16px 24px;
  display: flex;
  align-items: center;
  z-index: 2;
}

/* Custom Radio/Checkbox Styles */
$clouds: #ecf0f1;
$midnight: var(--v-primary);
$wisteria: white;

input[type="checkbox"],
input[type="radio"] {
  position: absolute;
  opacity: 0;
  z-index: -1;
}
label {
  position: relative;
  margin-right: 1em;
  padding-left: 2em;
  padding-right: 1em;
  line-height: 2;
  cursor: pointer;
  &:before {
    box-sizing: border-box;
    content: " ";
    position: absolute;
    top: 0.3em;
    left: 0;
    display: block;
    width: 1.4em;
    height: 1.4em;
    border: 2px solid var(--v-primary);
    border-radius: 0.25em;
    z-index: -1;
  }
}
input[type="radio"] + label::before {
  border-radius: 1em;
}
.selectsDisabled + label {
  cursor: not-allowed;
  &:before {
    border: 2px solid white;
    background: grey !important;
  }
}
input[type="checkbox"]:checked + label,
input[type="radio"]:checked + label {
  padding-left: 1em;
  color: $wisteria;
  &:before {
    top: 0;
    width: 100%;
    height: 2em;
    background: var(--v-primary);
  }
}
label,
label::before {
  transition: 0.25s all ease;
}
.history-wrapper {
  line-height: 1.2;
  padding: 4px 0;
  border-left: 2px solid var(--v-orange-base); /* Ein kleiner Akzent links wirkt oft "geiler" */
  padding-left: 8px;
}

.x-small-text {
  font-size: 0.65rem !important;
}

.cursor-pointer {
  cursor: pointer;
}

/* Verhindert, dass langer Text das Layout zersprengt */
.history-wrapper span {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 90vw;
}
</style>
